import { useState } from 'react';
import { ChevronDown, ChevronRight, Folder, FolderOpen } from 'lucide-react';
import { DocumentRow } from './DocumentRow';
import type {
  PortalDocument,
  PortalDocumentFolder,
} from '../services/documents-service';

export type FolderInitialState = 'expanded' | 'collapsed';

interface FolderNode {
  folder: PortalDocumentFolder;
  documents: PortalDocument[];
  subfolders: FolderNode[];
  documentCount: number;
}

function buildFolderNodes(
  parentFolderId: string | null,
  folders: PortalDocumentFolder[],
  documents: PortalDocument[]
): FolderNode[] {
  return folders
    .filter((folder) => folder.parent_folder_id === parentFolderId)
    .map((folder) => {
      const subfolders = buildFolderNodes(folder.id, folders, documents);
      const folderDocuments = documents.filter(
        (doc) => doc.folder_id === folder.id
      );
      const documentCount = subfolders.reduce(
        (sum, subfolder) => sum + subfolder.documentCount,
        folderDocuments.length
      );
      return { folder, documents: folderDocuments, subfolders, documentCount };
    })
    .filter((node) => node.documentCount > 0);
}

interface DocumentFolderSectionProps {
  node: FolderNode;
  onDownload: (documentId: string, filename: string) => void;
  downloadingId: string | null;
  initialFolderState: FolderInitialState;
}

function DocumentFolderSection({
  node,
  onDownload,
  downloadingId,
  initialFolderState,
}: DocumentFolderSectionProps) {
  const [isExpanded, setIsExpanded] = useState(
    initialFolderState === 'expanded'
  );

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        className="w-full flex items-center gap-2 py-2 px-2 rounded-md hover:bg-muted/50 transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        {isExpanded ? (
          <FolderOpen className="h-4 w-4 text-muted-foreground" />
        ) : (
          <Folder className="h-4 w-4 text-muted-foreground" />
        )}
        <span className="text-sm font-medium text-foreground">
          {node.folder.name}
        </span>
        <span className="text-xs text-muted-foreground">
          ({node.documentCount})
        </span>
      </button>
      {isExpanded && (
        <div className="ml-4">
          {node.subfolders.map((subfolder) => (
            <DocumentFolderSection
              key={subfolder.folder.id}
              node={subfolder}
              onDownload={onDownload}
              downloadingId={downloadingId}
              initialFolderState={initialFolderState}
            />
          ))}
          {node.documents.map((doc) => (
            <DocumentRow
              key={doc.id}
              document={doc}
              onDownload={onDownload}
              isDownloading={downloadingId === doc.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface DocumentFolderTreeProps {
  folders: PortalDocumentFolder[];
  documents: PortalDocument[];
  onDownload: (documentId: string, filename: string) => void;
  downloadingId: string | null;
  initialFolderState: FolderInitialState;
}

/** Collapsible fund folders; folders without documents are hidden. */
export function DocumentFolderTree({
  folders,
  documents,
  onDownload,
  downloadingId,
  initialFolderState,
}: DocumentFolderTreeProps) {
  return buildFolderNodes(null, folders, documents).map((node) => (
    <DocumentFolderSection
      key={node.folder.id}
      node={node}
      onDownload={onDownload}
      downloadingId={downloadingId}
      initialFolderState={initialFolderState}
    />
  ));
}
